import React from "react";
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ExpandMore,
  CheckCircle,
  TipsAndUpdates,
  ThumbUp,
  AutoAwesome,
  Psychology,
} from "@mui/icons-material";
import { EvaluationDetailV2_0_0 } from "../../../types";
import TopicBadges from "../../common/TopicBadges";

interface Props {
  evaluation: EvaluationDetailV2_0_0;
}

interface ScoreRowProps {
  label: string;
  score: number;
  maxScore: number;
  explanation: string;
}

const ScoreRow: React.FC<ScoreRowProps> = ({ label, score, maxScore, explanation }) => {
  const percentage = (score / maxScore) * 100;
  const ratio = score / maxScore;
  const color: "success" | "primary" | "warning" | "error" =
    ratio >= 0.8 ? "success" : ratio >= 0.6 ? "primary" : ratio >= 0.4 ? "warning" : "error";

  return (
    <Box sx={{ mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="body2" fontWeight="medium">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight="bold" color={`${color}.main`}>
          {score} / {maxScore}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        color={color}
        sx={{ height: 8, borderRadius: 1 }}
      />
      {explanation && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {explanation}
        </Typography>
      )}
    </Box>
  );
};

const EvaluationDetailV2_0_0View: React.FC<Props> = ({ evaluation }) => {
  const { understanding, logicOriginality, structureExpression, improvement } = evaluation.result;
  const claimTypeForBadge =
    evaluation.topic.claimType == null ? undefined : evaluation.topic.claimType;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 3, bgcolor: "grey.50" }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          <Psychology sx={{ mr: 1, verticalAlign: "middle" }} />
          カテゴリ別スコア
        </Typography>
        <ScoreRow
          label="理解度"
          score={understanding.scores.understanding}
          maxScore={25}
          explanation={understanding.scoreExplanations.understanding}
        />
        <ScoreRow
          label="論理性"
          score={logicOriginality.scores.logic}
          maxScore={25}
          explanation={logicOriginality.scoreExplanations.logic}
        />
        <ScoreRow
          label="独創性"
          score={logicOriginality.scores.originality}
          maxScore={20}
          explanation={logicOriginality.scoreExplanations.originality}
        />
        <ScoreRow
          label="構成"
          score={structureExpression.scores.structure}
          maxScore={15}
          explanation={structureExpression.scoreExplanations.structure}
        />
        <ScoreRow
          label="表現力"
          score={structureExpression.scores.expression}
          maxScore={15}
          explanation={structureExpression.scoreExplanations.expression}
        />
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          <AutoAwesome sx={{ mr: 1, verticalAlign: "middle" }} />
          総評
        </Typography>
        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
          {improvement.shortReview}
        </Typography>
      </Paper>

      {improvement.strengths.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "success.50" }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="success.main">
            <ThumbUp sx={{ mr: 1, verticalAlign: "middle" }} />
            良い点
          </Typography>
          <List dense disablePadding>
            {improvement.strengths.map((strength, index) => (
              <ListItem key={index} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={strength} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {improvement.improvements.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "info.50" }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="info.main">
            <TipsAndUpdates sx={{ mr: 1, verticalAlign: "middle" }} />
            改善提案
          </Typography>
          <List dense disablePadding>
            {improvement.improvements.map((item, index) => (
              <ListItem key={index} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Typography variant="body2" fontWeight="bold" color="info.main">
                    {index + 1}.
                  </Typography>
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              元の問題文
            </Typography>
            <TopicBadges
              difficulty={evaluation.topic.difficulty}
              genre={evaluation.topic.genre}
              questionType={evaluation.topic.questionType}
              claimType={claimTypeForBadge}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {evaluation.topic.theme && (
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {evaluation.topic.theme}
            </Typography>
          )}
          {evaluation.topic.content && (
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {evaluation.topic.content}
            </Typography>
          )}
          {evaluation.topic.background && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                背景説明
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {evaluation.topic.background}
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {evaluation.input.understanding && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              ユーザーの理解
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              主張
            </Typography>
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: "grey.50" }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {evaluation.input.understanding.claim || "(未入力)"}
              </Typography>
            </Paper>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              根拠
            </Typography>
            <Paper sx={{ p: 1.5, bgcolor: "grey.50" }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {evaluation.input.understanding.grounds || "(未入力)"}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="bold">
            ユーザーの回答
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {evaluation.input.userOpinion}
            </Typography>
          </Paper>
        </AccordionDetails>
      </Accordion>

      {improvement.improvedExample && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1" fontWeight="bold">
              AI改善例
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Paper sx={{ p: 2, bgcolor: "primary.50" }}>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {improvement.improvedExample}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default EvaluationDetailV2_0_0View;
